import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useNotifications } from '../../../hooks/useNotifications';

type RSVPStatus = 'going' | 'maybe' | 'not-going' | null;

type Event = {
  id: number;
  title: string;
  date: number;
  month: string;
  year: number;
  time: string;
  type: string;
  typeLabel: string;
  typeColor: string;
  attendees: number;
  maybeCount: number;
  isLive: boolean;
  isReminded: boolean;
  rsvpStatus: RSVPStatus;
};

const CommunityEventsCalendar = () => {
  const { user } = useAuth();
  const { createNotification } = useNotifications();
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [reminderTime, setReminderTime] = useState('30min');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Current month for calendar view
  const [currentMonth, setCurrentMonth] = useState(new Date(2025, 0)); // January 2025
  
  const [events, setEvents] = useState<Event[]>([
    {
      id: 1,
      title: 'Weekly Watch Party: Desert Rose Finale',
      date: 15,
      month: 'Jan',
      year: 2025,
      time: '8:00 PM GST',
      type: 'watch-party',
      typeLabel: 'Watch Party',
      typeColor: 'bg-emerald-100 text-emerald-700',
      attendees: 234,
      maybeCount: 45,
      isLive: true,
      isReminded: false,
      rsvpStatus: null
    },
    {
      id: 2,
      title: 'Creator AMA: Director Ahmad Khalil',
      date: 17,
      month: 'Jan',
      year: 2025,
      time: '6:00 PM GST',
      type: 'ama',
      typeLabel: 'AMA',
      typeColor: 'bg-rose-100 text-rose-700',
      attendees: 156,
      maybeCount: 32,
      isLive: false,
      isReminded: false,
      rsvpStatus: null
    },
    {
      id: 3,
      title: 'New Drama Premiere: Echoes of Beirut',
      date: 20,
      month: 'Jan',
      year: 2025,
      time: '9:00 PM GST',
      type: 'premiere',
      typeLabel: 'Premiere',
      typeColor: 'bg-purple-100 text-purple-700',
      attendees: 512,
      maybeCount: 89,
      isLive: false,
      isReminded: true,
      rsvpStatus: 'going'
    },
    {
      id: 4,
      title: 'Community Trivia Night',
      date: 25,
      month: 'Jan',
      year: 2025,
      time: '7:00 PM GST',
      type: 'trivia',
      typeLabel: 'Trivia',
      typeColor: 'bg-cyan-100 text-cyan-700',
      attendees: 178,
      maybeCount: 56,
      isLive: false,
      isReminded: false,
      rsvpStatus: 'maybe'
    },
    {
      id: 5,
      title: 'Fan Art Showcase',
      date: 28,
      month: 'Jan',
      year: 2025,
      time: '5:00 PM GST',
      type: 'showcase',
      typeLabel: 'Showcase',
      typeColor: 'bg-amber-100 text-amber-700',
      attendees: 98,
      maybeCount: 24,
      isLive: false,
      isReminded: false,
      rsvpStatus: null
    }
  ]);

  // Calendar helpers
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days: (number | null)[] = [];
    
    // Add empty slots for days before the first day
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    // Add the days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return days;
  }, [currentMonth]);

  const getEventsForDay = (day: number) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonthName = monthNames[currentMonth.getMonth()];
    return events.filter(e => e.date === day && e.month === currentMonthName);
  };

  const handleSetReminder = (event: Event) => {
    if (!user) {
      alert('Please sign in to set reminders');
      return;
    }
    setSelectedEvent(event);
    setShowReminderModal(true);
  };

  const handleRSVP = (event: Event) => {
    if (!user) {
      alert('Please sign in to RSVP');
      return;
    }
    setSelectedEvent(event);
    setShowRSVPModal(true);
  };

  const handleShareEvent = (event: Event) => {
    setSelectedEvent(event);
    setShowShareModal(true);
  };

  const getEventShareUrl = (event: Event) => {
    return `${window.location.origin}/events/${event.id}`;
  };

  const getEventShareText = (event: Event) => {
    return `Join me at "${event.title}" on ${event.month} ${event.date}, ${event.year} at ${event.time}! 🎉`;
  };

  const handleCopyEventLink = async () => {
    if (!selectedEvent) return;
    try {
      await navigator.clipboard.writeText(getEventShareUrl(selectedEvent));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const shareToTwitter = () => {
    if (!selectedEvent) return;
    const text = getEventShareText(selectedEvent);
    const url = getEventShareUrl(selectedEvent);
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareToFacebook = () => {
    if (!selectedEvent) return;
    const url = getEventShareUrl(selectedEvent);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };

  const shareToWhatsApp = () => {
    if (!selectedEvent) return;
    const text = `${getEventShareText(selectedEvent)} ${getEventShareUrl(selectedEvent)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareToTelegram = () => {
    if (!selectedEvent) return;
    const text = getEventShareText(selectedEvent);
    const url = getEventShareUrl(selectedEvent);
    window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareViaEmail = () => {
    if (!selectedEvent) return;
    const subject = `You're invited: ${selectedEvent.title}`;
    const body = `Hey!\n\nI wanted to invite you to this event:\n\n${selectedEvent.title}\n📅 ${selectedEvent.month} ${selectedEvent.date}, ${selectedEvent.year}\n⏰ ${selectedEvent.time}\n\nJoin me here: ${getEventShareUrl(selectedEvent)}\n\nHope to see you there!`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  const shareViaSMS = () => {
    if (!selectedEvent) return;
    const text = `${getEventShareText(selectedEvent)} ${getEventShareUrl(selectedEvent)}`;
    window.open(`sms:?body=${encodeURIComponent(text)}`, '_blank');
  };

  const confirmRSVP = async (status: RSVPStatus) => {
    if (!selectedEvent || !user) return;

    const previousStatus = selectedEvent.rsvpStatus;
    
    // Update local state with RSVP and adjust counts
    setEvents(prev => prev.map(e => {
      if (e.id === selectedEvent.id) {
        let newAttendees = e.attendees;
        let newMaybeCount = e.maybeCount;
        
        // Remove from previous count
        if (previousStatus === 'going') newAttendees--;
        if (previousStatus === 'maybe') newMaybeCount--;
        
        // Add to new count
        if (status === 'going') newAttendees++;
        if (status === 'maybe') newMaybeCount++;
        
        return { 
          ...e, 
          rsvpStatus: status,
          attendees: newAttendees,
          maybeCount: newMaybeCount
        };
      }
      return e;
    }));

    // Create notification
    const statusText = status === 'going' ? "You're going to" : status === 'maybe' ? "You might attend" : "You declined";
    await createNotification(
      user.id,
      'event_rsvp',
      'RSVP Updated',
      `${statusText} "${selectedEvent.title}"`,
      { eventId: selectedEvent.id, rsvpStatus: status }
    );

    setShowRSVPModal(false);
    setSuccessMessage(status === 'going' ? "You're going!" : status === 'maybe' ? 'Marked as maybe' : 'RSVP removed');
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const confirmReminder = async () => {
    if (!selectedEvent || !user) return;

    setEvents(prev => prev.map(e => 
      e.id === selectedEvent.id ? { ...e, isReminded: true } : e
    ));

    await createNotification(
      user.id,
      'event_reminder',
      'Event Reminder Set',
      `You'll be reminded ${reminderTime === '30min' ? '30 minutes' : reminderTime === '1hour' ? '1 hour' : '1 day'} before "${selectedEvent.title}"`,
      { eventId: selectedEvent.id, reminderTime }
    );

    setShowReminderModal(false);
    setSuccessMessage('Reminder set successfully!');
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const removeReminder = async (event: Event) => {
    setEvents(prev => prev.map(e => 
      e.id === event.id ? { ...e, isReminded: false } : e
    ));

    if (user) {
      await createNotification(
        user.id,
        'event_reminder_removed',
        'Reminder Removed',
        `Reminder for "${event.title}" has been removed`,
        { eventId: event.id }
      );
    }
  };

  const getRSVPButtonStyle = (status: RSVPStatus) => {
    switch (status) {
      case 'going':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'maybe':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'not-going':
        return 'bg-gray-100 text-gray-500 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-teal-50 hover:text-teal-600 hover:border-teal-200';
    }
  };

  const getRSVPIcon = (status: RSVPStatus) => {
    switch (status) {
      case 'going':
        return 'ri-check-line';
      case 'maybe':
        return 'ri-question-line';
      case 'not-going':
        return 'ri-close-line';
      default:
        return 'ri-calendar-check-line';
    }
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-emerald-500 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg">
                <i className="ri-calendar-event-line text-white text-lg"></i>
              </div>
              <h3 className="font-semibold text-white">Upcoming Events</h3>
            </div>
            <div className="flex items-center gap-2">
              {/* View Toggle */}
              <div className="flex items-center bg-white/20 rounded-lg p-0.5">
                <button
                  onClick={() => setViewMode('list')}
                  className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                    viewMode === 'list' ? 'bg-white text-teal-600' : 'text-white/80 hover:text-white'
                  }`}
                  title="List view"
                >
                  <i className="ri-list-unordered text-sm"></i>
                </button>
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                    viewMode === 'calendar' ? 'bg-white text-teal-600' : 'text-white/80 hover:text-white'
                  }`}
                  title="Calendar view"
                >
                  <i className="ri-calendar-line text-sm"></i>
                </button>
              </div>
              <Link 
                to="/forum" 
                className="text-white/80 hover:text-white text-xs font-medium flex items-center whitespace-nowrap"
              >
                View All
                <i className="ri-arrow-right-s-line ml-0.5"></i>
              </Link>
            </div>
          </div>
        </div>

        {/* Calendar View */}
        {viewMode === 'calendar' && (
          <div className="p-3">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-3">
              <button 
                onClick={prevMonth}
                className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
              </button>
              <span className="text-sm font-semibold text-gray-800">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </span>
              <button 
                onClick={nextMonth}
                className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-lg"></i>
              </button>
            </div>

            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                <div key={day} className="text-center text-[10px] font-medium text-gray-400 py-1">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => {
                const dayEvents = day ? getEventsForDay(day) : [];
                const hasEvents = dayEvents.length > 0;
                const hasLiveEvent = dayEvents.some(e => e.isLive);
                const hasRSVP = dayEvents.some(e => e.rsvpStatus === 'going');
                
                return (
                  <div
                    key={index}
                    className={`relative aspect-square flex flex-col items-center justify-center rounded-lg text-xs transition-colors ${
                      day === null 
                        ? '' 
                        : hasLiveEvent
                          ? 'bg-red-50 text-red-600 font-semibold cursor-pointer hover:bg-red-100'
                          : hasEvents
                            ? 'bg-teal-50 text-teal-700 font-medium cursor-pointer hover:bg-teal-100'
                            : 'text-gray-600 hover:bg-gray-50 cursor-pointer'
                    }`}
                    onClick={() => {
                      if (dayEvents.length > 0) {
                        setSelectedEvent(dayEvents[0]);
                        setShowRSVPModal(true);
                      }
                    }}
                  >
                    {day}
                    {hasEvents && (
                      <div className="absolute bottom-0.5 flex gap-0.5">
                        {hasLiveEvent && (
                          <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse"></span>
                        )}
                        {!hasLiveEvent && hasRSVP && (
                          <span className="w-1 h-1 rounded-full bg-emerald-500"></span>
                        )}
                        {!hasLiveEvent && !hasRSVP && (
                          <span className="w-1 h-1 rounded-full bg-teal-500"></span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span className="text-[10px] text-gray-500">Live</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                <span className="text-[10px] text-gray-500">Going</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                <span className="text-[10px] text-gray-500">Event</span>
              </div>
            </div>
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="divide-y divide-gray-50">
            {events.map(event => (
              <div
                key={event.id}
                className="p-3 hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-start gap-3">
                  {/* Date Badge */}
                  <div className={`flex-shrink-0 w-11 h-11 rounded-lg flex flex-col items-center justify-center ${
                    event.isLive 
                      ? 'bg-red-50 border border-red-200' 
                      : event.rsvpStatus === 'going'
                        ? 'bg-emerald-50 border border-emerald-200'
                        : 'bg-teal-50 border border-teal-100'
                  }`}>
                    <span className={`text-[10px] font-medium ${
                      event.isLive ? 'text-red-500' : event.rsvpStatus === 'going' ? 'text-emerald-600' : 'text-teal-600'
                    }`}>
                      {event.month}
                    </span>
                    <span className={`text-base font-bold ${
                      event.isLive ? 'text-red-600' : event.rsvpStatus === 'going' ? 'text-emerald-700' : 'text-teal-700'
                    }`}>
                      {event.date}
                    </span>
                  </div>

                  {/* Event Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${event.typeColor}`}>
                        {event.typeLabel}
                      </span>
                      {event.isLive && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 flex items-center">
                          <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse mr-1"></span>
                          LIVE
                        </span>
                      )}
                      {event.rsvpStatus && !event.isLive && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex items-center ${
                          event.rsvpStatus === 'going' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : event.rsvpStatus === 'maybe'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-gray-100 text-gray-600'
                        }`}>
                          <i className={`${getRSVPIcon(event.rsvpStatus)} text-[10px] mr-0.5`}></i>
                          {event.rsvpStatus === 'going' ? 'Going' : event.rsvpStatus === 'maybe' ? 'Maybe' : 'Not Going'}
                        </span>
                      )}
                      {event.isReminded && !event.isLive && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 flex items-center">
                          <i className="ri-notification-3-line text-[10px] mr-0.5"></i>
                          Reminder
                        </span>
                      )}
                    </div>
                    <h4 className="text-sm font-medium text-gray-900 line-clamp-1 group-hover:text-teal-600 transition-colors cursor-pointer">
                      {event.title}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[11px] text-gray-500 flex items-center">
                        <i className="ri-time-line mr-0.5 text-gray-400"></i>
                        {event.time}
                      </span>
                      <span className="text-[11px] text-gray-500 flex items-center" title={`${event.attendees} going, ${event.maybeCount} maybe`}>
                        <i className="ri-user-line mr-0.5 text-gray-400"></i>
                        {event.attendees}
                        {event.maybeCount > 0 && (
                          <span className="text-gray-400 ml-0.5">(+{event.maybeCount})</span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex-shrink-0 flex items-center gap-1">
                    {event.isLive ? (
                      <>
                        <button className="px-2 py-1 bg-red-500 text-white text-[11px] font-medium rounded-md hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">
                          Join Now
                        </button>
                        {/* Share Button for Live Events */}
                        <button 
                          onClick={() => handleShareEvent(event)}
                          className="w-7 h-7 flex items-center justify-center bg-gray-100 text-gray-500 rounded-md hover:bg-teal-100 hover:text-teal-600 transition-colors cursor-pointer"
                          title="Invite friends"
                        >
                          <i className="ri-share-line text-sm"></i>
                        </button>
                      </>
                    ) : (
                      <>
                        {/* RSVP Button */}
                        <button 
                          onClick={() => handleRSVP(event)}
                          className={`px-2 py-1 text-[11px] font-medium rounded-md border transition-colors cursor-pointer whitespace-nowrap ${getRSVPButtonStyle(event.rsvpStatus)}`}
                          title="RSVP"
                        >
                          <i className={`${getRSVPIcon(event.rsvpStatus)} mr-0.5`}></i>
                          {event.rsvpStatus ? (event.rsvpStatus === 'going' ? 'Going' : event.rsvpStatus === 'maybe' ? 'Maybe' : 'Can\'t Go') : 'RSVP'}
                        </button>
                        
                        {/* Share Button */}
                        <button 
                          onClick={() => handleShareEvent(event)}
                          className="w-7 h-7 flex items-center justify-center bg-gray-100 text-gray-500 rounded-md hover:bg-teal-100 hover:text-teal-600 transition-colors cursor-pointer"
                          title="Invite friends"
                        >
                          <i className="ri-share-line text-sm"></i>
                        </button>
                        
                        {/* Reminder Button */}
                        {event.isReminded ? (
                          <button 
                            onClick={() => removeReminder(event)}
                            className="w-7 h-7 flex items-center justify-center bg-amber-100 text-amber-600 rounded-md hover:bg-amber-200 transition-colors cursor-pointer"
                            title="Remove reminder"
                          >
                            <i className="ri-notification-off-line text-sm"></i>
                          </button>
                        ) : (
                          <button 
                            onClick={() => handleSetReminder(event)}
                            className="w-7 h-7 flex items-center justify-center bg-gray-100 text-gray-500 rounded-md hover:bg-teal-100 hover:text-teal-600 transition-colors cursor-pointer"
                            title="Set reminder"
                          >
                            <i className="ri-notification-3-line text-sm"></i>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="p-3 bg-gray-50 border-t border-gray-100">
          {/* Removed "Create Event" button */}
        </div>
      </div>

      {/* RSVP Modal */}
      {showRSVPModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">RSVP to Event</h3>
                <button 
                  onClick={() => setShowRSVPModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            
            <div className="p-4">
              <div className="flex items-start gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                <div className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center flex-shrink-0 ${
                  selectedEvent.isLive ? 'bg-red-100' : 'bg-teal-100'
                }`}>
                  <span className={`text-[10px] font-medium ${selectedEvent.isLive ? 'text-red-600' : 'text-teal-600'}`}>
                    {selectedEvent.month}
                  </span>
                  <span className={`text-sm font-bold ${selectedEvent.isLive ? 'text-red-700' : 'text-teal-700'}`}>
                    {selectedEvent.date}
                  </span>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900">{selectedEvent.title}</h4>
                  <p className="text-xs text-gray-500 mt-0.5">{selectedEvent.time}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[11px] text-gray-500">
                      <i className="ri-user-line mr-0.5"></i>
                      {selectedEvent.attendees} going
                    </span>
                    {selectedEvent.maybeCount > 0 && (
                      <span className="text-[11px] text-gray-400">
                        {selectedEvent.maybeCount} maybe
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-3">
                Will you attend?
              </label>
              <div className="space-y-2">
                <button 
                  onClick={() => confirmRSVP('going')}
                  className={`flex items-center w-full p-3 rounded-lg border transition-colors cursor-pointer ${
                    selectedEvent.rsvpStatus === 'going' 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                    selectedEvent.rsvpStatus === 'going' ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-600'
                  }`}>
                    <i className="ri-check-line text-lg"></i>
                  </div>
                  <div className="text-left">
                    <span className="text-sm font-medium text-gray-900">Going</span>
                    <p className="text-xs text-gray-500">I'll be there!</p>
                  </div>
                  {selectedEvent.rsvpStatus === 'going' && (
                    <i className="ri-check-line text-emerald-500 ml-auto text-lg"></i>
                  )}
                </button>

                <button 
                  onClick={() => confirmRSVP('maybe')}
                  className={`flex items-center w-full p-3 rounded-lg border transition-colors cursor-pointer ${
                    selectedEvent.rsvpStatus === 'maybe' 
                      ? 'border-amber-500 bg-amber-50' 
                      : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                    selectedEvent.rsvpStatus === 'maybe' ? 'bg-amber-500 text-white' : 'bg-amber-100 text-amber-600'
                  }`}>
                    <i className="ri-question-line text-lg"></i>
                  </div>
                  <div className="text-left">
                    <span className="text-sm font-medium text-gray-900">Maybe</span>
                    <p className="text-xs text-gray-500">I might attend</p>
                  </div>
                  {selectedEvent.rsvpStatus === 'maybe' && (
                    <i className="ri-check-line text-amber-500 ml-auto text-lg"></i>
                  )}
                </button>

                <button 
                  onClick={() => confirmRSVP('not-going')}
                  className={`flex items-center w-full p-3 rounded-lg border transition-colors cursor-pointer ${
                    selectedEvent.rsvpStatus === 'not-going' 
                      ? 'border-gray-400 bg-gray-50' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                    selectedEvent.rsvpStatus === 'not-going' ? 'bg-gray-500 text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    <i className="ri-close-line text-lg"></i>
                  </div>
                  <div className="text-left">
                    <span className="text-sm font-medium text-gray-900">Can't Go</span>
                    <p className="text-xs text-gray-500">I won't be able to make it</p>
                  </div>
                  {selectedEvent.rsvpStatus === 'not-going' && (
                    <i className="ri-check-line text-gray-500 ml-auto text-lg"></i>
                  )}
                </button>
              </div>

              {selectedEvent.rsvpStatus && (
                <button 
                  onClick={() => confirmRSVP(null)}
                  className="w-full mt-3 py-2 text-gray-500 text-sm hover:text-gray-700 transition-colors cursor-pointer"
                >
                  Remove RSVP
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reminder Modal */}
      {showReminderModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Set Event Reminder</h3>
                <button 
                  onClick={() => setShowReminderModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            
            <div className="p-4">
              <div className="flex items-start gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex flex-col items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-medium text-teal-600">{selectedEvent.month}</span>
                  <span className="text-sm font-bold text-teal-700">{selectedEvent.date}</span>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900">{selectedEvent.title}</h4>
                  <p className="text-xs text-gray-500 mt-0.5">{selectedEvent.time}</p>
                </div>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remind me before
              </label>
              <div className="space-y-2">
                {[
                  { value: '30min', label: '30 minutes before' },
                  { value: '1hour', label: '1 hour before' },
                  { value: '1day', label: '1 day before' }
                ].map(option => (
                  <label 
                    key={option.value}
                    className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                      reminderTime === option.value 
                        ? 'border-teal-500 bg-teal-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="reminderTime"
                      value={option.value}
                      checked={reminderTime === option.value}
                      onChange={(e) => setReminderTime(e.target.value)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                      reminderTime === option.value 
                        ? 'border-teal-500' 
                        : 'border-gray-300'
                    }`}>
                      {reminderTime === option.value && (
                        <div className="w-2 h-2 rounded-full bg-teal-500"></div>
                      )}
                    </div>
                    <span className="text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>

              <div className="flex items-center gap-2 mt-4 p-3 bg-amber-50 rounded-lg">
                <i className="ri-notification-3-line text-amber-600"></i>
                <p className="text-xs text-amber-700">
                  You'll receive a notification when the event is about to start
                </p>
              </div>
            </div>

            <div className="p-4 bg-gray-50 border-t border-gray-100 flex gap-2">
              <button 
                onClick={() => setShowReminderModal(false)}
                className="flex-1 py-2 text-gray-600 text-sm font-medium hover:text-gray-800 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button 
                onClick={confirmReminder}
                className="flex-1 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-notification-3-line mr-1"></i>
                Set Reminder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {showSuccess && (
        <div className="fixed bottom-4 right-4 bg-emerald-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50 animate-slide-up">
          <i className="ri-check-line text-lg"></i>
          <span className="text-sm font-medium">{successMessage}</span>
        </div>
      )}

      {/* Share Event Modal */}
      {showShareModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Invite Friends</h3>
                <button 
                  onClick={() => setShowShareModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            
            <div className="p-4">
              {/* Event Preview */}
              <div className="flex items-start gap-3 mb-4 p-3 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg border border-teal-100">
                <div className={`w-12 h-12 rounded-lg flex flex-col items-center justify-center flex-shrink-0 ${
                  selectedEvent.isLive ? 'bg-red-100' : 'bg-white'
                }`}>
                  <span className={`text-[10px] font-medium ${selectedEvent.isLive ? 'text-red-600' : 'text-teal-600'}`}>
                    {selectedEvent.month}
                  </span>
                  <span className={`text-base font-bold ${selectedEvent.isLive ? 'text-red-700' : 'text-teal-700'}`}>
                    {selectedEvent.date}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium mb-1 ${selectedEvent.typeColor}`}>
                    {selectedEvent.typeLabel}
                  </span>
                  <h4 className="text-sm font-medium text-gray-900 line-clamp-2">{selectedEvent.title}</h4>
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center">
                    <i className="ri-time-line mr-1"></i>
                    {selectedEvent.time}
                  </p>
                </div>
              </div>

              {/* Invite Message */}
              <div className="flex items-center gap-2 mb-4 p-2 bg-amber-50 rounded-lg">
                <i className="ri-user-add-line text-amber-600"></i>
                <p className="text-xs text-amber-700">
                  Share this event with friends and watch together!
                </p>
              </div>

              {/* Quick Share Buttons */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                <button 
                  onClick={shareToWhatsApp}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-green-50 hover:bg-green-100 transition-colors cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-green-500 rounded-full group-hover:scale-110 transition-transform">
                    <i className="ri-whatsapp-line text-white text-lg"></i>
                  </div>
                  <span className="text-[10px] font-medium text-gray-600">WhatsApp</span>
                </button>
                
                <button 
                  onClick={shareToTelegram}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-sky-50 hover:bg-sky-100 transition-colors cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-sky-500 rounded-full group-hover:scale-110 transition-transform">
                    <i className="ri-telegram-line text-white text-lg"></i>
                  </div>
                  <span className="text-[10px] font-medium text-gray-600">Telegram</span>
                </button>
                
                <button 
                  onClick={shareToTwitter}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-black rounded-full group-hover:scale-110 transition-transform">
                    <i className="ri-twitter-x-line text-white text-lg"></i>
                  </div>
                  <span className="text-[10px] font-medium text-gray-600">Twitter</span>
                </button>
                
                <button 
                  onClick={shareToFacebook}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-colors cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-indigo-600 rounded-full group-hover:scale-110 transition-transform">
                    <i className="ri-facebook-fill text-white text-lg"></i>
                  </div>
                  <span className="text-[10px] font-medium text-gray-600">Facebook</span>
                </button>
              </div>

              {/* More Share Options */}
              <div className="space-y-2">
                <button 
                  onClick={shareViaEmail}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-rose-100 rounded-lg">
                    <i className="ri-mail-line text-rose-600"></i>
                  </div>
                  <span className="text-sm font-medium text-gray-700">Send via Email</span>
                  <i className="ri-arrow-right-s-line text-gray-400 ml-auto"></i>
                </button>
                
                <button 
                  onClick={shareViaSMS}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-message-2-line text-teal-600"></i>
                  </div>
                  <span className="text-sm font-medium text-gray-700">Send via SMS</span>
                  <i className="ri-arrow-right-s-line text-gray-400 ml-auto"></i>
                </button>
              </div>

              {/* Copy Link Section */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <label className="block text-xs font-medium text-gray-500 mb-2">Or copy event link</label>
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <i className="ri-link text-gray-400"></i>
                  <input
                    type="text"
                    value={getEventShareUrl(selectedEvent)}
                    readOnly
                    className="flex-1 bg-transparent text-xs text-gray-600 outline-none truncate"
                  />
                  <button
                    onClick={handleCopyEventLink}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                      copied 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-teal-500 text-white hover:bg-teal-600'
                    }`}
                  >
                    {copied ? (
                      <>
                        <i className="ri-check-line mr-1"></i>
                        Copied!
                      </>
                    ) : (
                      <>
                        <i className="ri-file-copy-line mr-1"></i>
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default CommunityEventsCalendar;
